import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users, MessageCircle, DollarSign, AlertTriangle, 
  TrendingUp, Settings, Search, MoreVertical, 
  Ban, Trash2, CheckCircle, Eye, ArrowLeft, Crown,
  Package, Ticket, Megaphone, FileText, CreditCard
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  adminGetUsers, adminSuspendUser, adminUnsuspendUser, adminDeleteUser,
  adminGetReports, adminResolveReport, adminGetSettings, adminUpdateSettings,
  adminGetAnalytics
} from '../services/api';
import BottomNav from '../components/BottomNav';
import AdminUserDetailModal from '../components/AdminUserDetailModal';
import CreditPackagesTab from '../components/admin/CreditPackagesTab';
import PromoCodesTab from '../components/admin/PromoCodesTab';
import AuditLogTab from '../components/admin/AuditLogTab';
import TransactionsTab from '../components/admin/TransactionsTab';
import DefaultAvatarTab from '../components/admin/DefaultAvatarTab';
import DiscoverySettingsTab from '../components/admin/DiscoverySettingsTab';
import WelcomeImagesTab from '../components/admin/WelcomeImagesTab';
import PromoCreditsTab from '../components/admin/PromoCreditsTab';
import BroadcastsTab from '../components/admin/BroadcastsTab';
import PhotoModerationTab from '../components/admin/PhotoModerationTab';
import ContactMessagesTab from '../components/admin/ContactMessagesTab';
import { useDefaultAvatar } from '../services/defaultAvatar';

const AdminPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [reports, setReports] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [userFilter, setUserFilter] = useState('all'); // 'all' | 'premium'
  const [detailUserId, setDetailUserId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, usersRes, reportsRes, settingsRes] = await Promise.all([
        adminGetAnalytics(),
        adminGetUsers({ limit: 50 }),
        adminGetReports('pending'),
        adminGetSettings()
      ]);
      setAnalytics(analyticsRes.data);
      setUsers(usersRes.data.users);
      setReports(reportsRes.data);
      setSettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to load admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuspendUser = async (userId, suspended) => {
    try {
      if (suspended) {
        await adminUnsuspendUser(userId);
      } else {
        await adminSuspendUser(userId);
      }
      setUsers(users.map(u => 
        u.user_id === userId ? { ...u, suspended: !suspended } : u
      ));
    } catch (error) {
      console.error('Failed to suspend/unsuspend user:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await adminDeleteUser(userId);
      setUsers(users.filter(u => u.user_id !== userId));
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  const handleResolveReport = async (reportId) => {
    try {
      await adminResolveReport(reportId, 'Resolved by admin');
      setReports(reports.filter(r => r.report_id !== reportId));
    } catch (error) {
      console.error('Failed to resolve report:', error);
    }
  };

  const handleUpdateSettings = async (key, value) => {
    try {
      const newSettings = { ...settings, [key]: value };
      await adminUpdateSettings(newSettings);
      setSettings(newSettings);
    } catch (error) {
      console.error('Failed to update settings:', error);
    }
  };

  const defaultPhoto = useDefaultAvatar() || 'https://images.unsplash.com/photo-1650702970095-f5b8ebeebce5?w=100';

  const filteredUsers = users.filter(u => {
    const matchesSearch =
      u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = userFilter === 'premium' ? !!u.is_premium : true;
    return matchesSearch && matchesFilter;
  });

  const goToTab = (tab, filter = 'all') => {
    setUserFilter(filter);
    setActiveTab(tab);
    // Ensure the tabs are in view when clicked from the dashboard
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center pb-20">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#009E60] border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24" data-testid="admin-page">
      {/* Header */}
      <div className="fixed top-0 left-0 right-0 z-30 bg-white/95 backdrop-blur-lg border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-4">
          <button onClick={() => navigate('/discover')} className="p-2 -ml-2 hover:bg-gray-100 rounded-full">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>Admin Panel</h1>
            <p className="text-sm text-gray-500">Manage your platform</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="pt-20 px-4">
        <div className="max-w-4xl mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <div className="overflow-x-auto mb-6 -mx-4 px-4 sm:mx-0 sm:px-0">
              <TabsList className="inline-flex bg-white rounded-xl p-1 min-w-max">
                <TabsTrigger value="dashboard" className="rounded-lg px-4">Dashboard</TabsTrigger>
                <TabsTrigger value="users" className="rounded-lg px-4">Users</TabsTrigger>
                <TabsTrigger value="reports" className="rounded-lg px-4">Reports</TabsTrigger>
                <TabsTrigger value="packages" className="rounded-lg px-4" data-testid="tab-packages">Credit Packages</TabsTrigger>
                <TabsTrigger value="promos" className="rounded-lg px-4" data-testid="tab-promos">Promo Codes</TabsTrigger>
                <TabsTrigger value="promo-credits" className="rounded-lg px-4" data-testid="tab-promo-credits">Promo Credits</TabsTrigger>
                <TabsTrigger value="broadcasts" className="rounded-lg px-4" data-testid="tab-broadcasts">Broadcasts</TabsTrigger>
                <TabsTrigger value="photo-moderation" className="rounded-lg px-4" data-testid="tab-photo-moderation">Photo Moderation</TabsTrigger>
                <TabsTrigger value="contact-inbox" className="rounded-lg px-4" data-testid="tab-contact-inbox">Contact Inbox</TabsTrigger>
                <TabsTrigger value="default-avatar" className="rounded-lg px-4" data-testid="tab-default-avatar">Default Avatar</TabsTrigger>
                <TabsTrigger value="welcome-images" className="rounded-lg px-4" data-testid="tab-welcome-images">Welcome Pictures</TabsTrigger>
                <TabsTrigger value="discovery" className="rounded-lg px-4" data-testid="tab-discovery">Discovery</TabsTrigger>
                <TabsTrigger value="transactions" className="rounded-lg px-4" data-testid="tab-transactions">Transactions</TabsTrigger>
                <TabsTrigger value="audit" className="rounded-lg px-4" data-testid="tab-audit">Audit Log</TabsTrigger>
                <TabsTrigger value="settings" className="rounded-lg px-4">Settings</TabsTrigger>
              </TabsList>
            </div>

            {/* Dashboard Tab */}
            <TabsContent value="dashboard">
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <button
                  type="button"
                  onClick={() => goToTab('users', 'all')}
                  className="admin-stat-card text-left transition-all hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#009E60]/40 rounded-2xl"
                  data-testid="dashboard-card-total-users"
                  aria-label="View all users"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#009E60]/10 flex items-center justify-center">
                      <Users className="w-6 h-6 text-[#009E60]" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{analytics?.total_users || 0}</div>
                      <div className="text-sm text-gray-500">Total Users</div>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => goToTab('users', 'premium')}
                  className="admin-stat-card text-left transition-all hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#FFCE00]/50 rounded-2xl"
                  data-testid="dashboard-card-premium-users"
                  aria-label="View premium users"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-[#FFCE00]/20 flex items-center justify-center">
                      <Crown className="w-6 h-6 text-[#FFCE00]" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{analytics?.premium_users || 0}</div>
                      <div className="text-sm text-gray-500">Premium Users</div>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => goToTab('broadcasts')}
                  className="admin-stat-card text-left transition-all hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-blue-300 rounded-2xl"
                  data-testid="dashboard-card-messages"
                  aria-label="Open messaging / broadcasts"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <MessageCircle className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">{analytics?.total_messages || 0}</div>
                      <div className="text-sm text-gray-500">Messages</div>
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => goToTab('reports')}
                  className="admin-stat-card text-left transition-all hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-green-300 rounded-2xl"
                  data-testid="dashboard-card-revenue"
                  aria-label="View reports and revenue"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">${analytics?.total_revenue?.toFixed(2) || '0.00'}</div>
                      <div className="text-sm text-gray-500">Revenue</div>
                    </div>
                  </div>
                </button>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4">Quick Stats</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Matches</span>
                      <span className="font-semibold">{analytics?.total_matches || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Recent Signups (7d)</span>
                      <span className="font-semibold">{analytics?.recent_signups || 0}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Pending Reports</span>
                      <span className="font-semibold text-red-500">{analytics?.pending_reports || 0}</span>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-6 shadow-sm">
                  <h3 className="font-semibold text-gray-900 mb-4">Messaging Status</h3>
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                    <div>
                      <div className="font-medium">Messaging Mode</div>
                      <div className="text-sm text-gray-500">
                        {settings?.mode === 'paid' ? 'Paid messaging enabled' : 'Free messaging for all'}
                      </div>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                      settings?.mode === 'paid' ? 'bg-[#FFCE00]/20 text-[#FFCE00]' : 'bg-green-100 text-green-700'
                    }`}>
                      {settings?.mode === 'paid' ? 'PAID' : 'FREE'}
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Users Tab */}
            <TabsContent value="users">
              <div className="bg-white rounded-2xl shadow-sm">
                <div className="p-4 border-b border-gray-100 space-y-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search users..."
                      className="pl-10 rounded-xl"
                      data-testid="user-search"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setUserFilter('all')}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                        userFilter === 'all'
                          ? 'bg-[#009E60] text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      data-testid="user-filter-all"
                    >
                      All
                    </button>
                    <button
                      type="button"
                      onClick={() => setUserFilter('premium')}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium inline-flex items-center gap-1 transition-colors ${
                        userFilter === 'premium'
                          ? 'bg-[#FFCE00] text-gray-900'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      data-testid="user-filter-premium"
                    >
                      <Crown className="w-3.5 h-3.5" />
                      Premium
                    </button>
                  </div>
                </div>

                <div className="divide-y divide-gray-100">
                  {filteredUsers.map((user) => (
                    <div key={user.user_id} className="flex items-center gap-4 p-4 hover:bg-gray-50">
                      <img
                        src={user.photos?.[0] || defaultPhoto}
                        alt={user.name}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{user.name}</span>
                          {user.is_premium && (
                            <Crown className="w-4 h-4 text-[#FFCE00]" />
                          )}
                          {user.suspended && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded-full">Suspended</span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 truncate">{user.email}</div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="rounded-full" data-testid={`user-menu-${user.user_id}`}>
                            <MoreVertical className="w-5 h-5 text-gray-500" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setDetailUserId(user.user_id)} data-testid={`manage-${user.user_id}`}>
                            <CreditCard className="w-4 h-4 mr-2" />
                            Manage Credits & Premium
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setDetailUserId(user.user_id)} data-testid={`view-profile-${user.user_id}`}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Profile
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleSuspendUser(user.user_id, user.suspended)}>
                            <Ban className="w-4 h-4 mr-2" />
                            {user.suspended ? 'Unsuspend' : 'Suspend'}
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-red-600" onClick={() => handleDeleteUser(user.user_id)}>
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Reports Tab */}
            <TabsContent value="reports">
              <div className="bg-white rounded-2xl shadow-sm">
                {reports.length === 0 ? (
                  <div className="p-8 text-center">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <h3 className="font-semibold text-gray-900 mb-1">No pending reports</h3>
                    <p className="text-gray-500">All reports have been resolved</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {reports.map((report) => (
                      <div key={report.report_id} className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <AlertTriangle className="w-4 h-4 text-red-500" />
                              <span className="font-medium text-gray-900">{report.reason}</span>
                            </div>
                            <p className="text-sm text-gray-600 mb-2">{report.details}</p>
                            <div className="text-xs text-gray-400">
                              Reported user: {report.reported_user_id}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleResolveReport(report.report_id)}
                            className="bg-[#009E60] hover:bg-[#007A4B] rounded-full"
                            data-testid={`resolve-${report.report_id}`}
                          >
                            Resolve
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Credit Packages Tab */}
            <TabsContent value="packages">
              <CreditPackagesTab />
            </TabsContent>

            {/* Promo Codes Tab */}
            <TabsContent value="promos">
              <PromoCodesTab />
            </TabsContent>

            <TabsContent value="promo-credits">
              <PromoCreditsTab />
            </TabsContent>

            {/* Default Avatar Tab */}
            <TabsContent value="default-avatar">
              <DefaultAvatarTab />
            </TabsContent>

            {/* Welcome Images Tab */}
            <TabsContent value="welcome-images">
              <WelcomeImagesTab />
            </TabsContent>

            {/* Discovery Settings Tab */}
            <TabsContent value="discovery">
              <DiscoverySettingsTab />
            </TabsContent>

            {/* Transactions Tab */}
            <TabsContent value="transactions">
              <TransactionsTab />
            </TabsContent>

            {/* Audit Log Tab */}
            <TabsContent value="audit">
              <AuditLogTab />
            </TabsContent>

            {/* Broadcasts Tab */}
            <TabsContent value="broadcasts">
              <BroadcastsTab />
            </TabsContent>

            {/* Photo Moderation Tab */}
            <TabsContent value="photo-moderation">
              <PhotoModerationTab />
            </TabsContent>

            {/* Contact Inbox Tab */}
            <TabsContent value="contact-inbox">
              <ContactMessagesTab />
            </TabsContent>

            {/* Settings Tab */}
            <TabsContent value="settings">
              <div className="bg-white rounded-2xl p-6 shadow-sm space-y-6">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-4">Messaging Settings</h3>
                  
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl mb-4">
                    <div>
                      <div className="font-medium text-gray-900">Paid Messaging</div>
                      <div className="text-sm text-gray-500">
                        Require subscription or credits to send messages
                      </div>
                    </div>
                    <Switch
                      checked={settings?.mode === 'paid'}
                      onCheckedChange={(checked) => handleUpdateSettings('mode', checked ? 'paid' : 'free')}
                      data-testid="messaging-mode-switch"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-gray-700">Subscription Price ($/month)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={settings?.subscription_price || 9.99}
                        onChange={(e) => handleUpdateSettings('subscription_price', parseFloat(e.target.value))}
                        className="mt-1.5 rounded-xl"
                        data-testid="subscription-price"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700">Credits Price ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={settings?.credits_price || 4.99}
                        onChange={(e) => handleUpdateSettings('credits_price', parseFloat(e.target.value))}
                        className="mt-1.5 rounded-xl"
                        data-testid="credits-price"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-700">Credits per Message</Label>
                      <Input
                        type="number"
                        value={settings?.credits_per_message || 1}
                        onChange={(e) => handleUpdateSettings('credits_per_message', parseInt(e.target.value))}
                        className="mt-1.5 rounded-xl"
                        data-testid="credits-per-message"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <BottomNav />

      {/* User Detail Modal */}
      {detailUserId && (
        <AdminUserDetailModal
          userId={detailUserId}
          onClose={() => setDetailUserId(null)}
          onUpdated={loadData}
        />
      )}
    </div>
  );
};

export default AdminPage;
